import { useState, useEffect, useCallback } from "react";
import { Search, RefreshCw, Phone, Play } from "lucide-react";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CallDetailModal } from "../components/CallDetailModal";

// Types
interface FirefliesTranscript {
  _id: string;
  firefliesId: string;
  title: string;
  date: number;
  duration: number;
  outcome: string | null;
  prospectName: string | null;
  prospectEmail: string | null;
  prospectPhone: string | null;
  sourceFunnel: string | null;
  sourcePlatform: string | null;
  sourceContent: string | null;
  closerName: string | null;
}

interface TranscriptListResponse {
  items: FirefliesTranscript[];
  totalCount: number;
}

const firstName = (name: string | null) => {
  if (!name) return null;
  return name.split(" ")[0] ?? null;
};
const lastName = (name: string | null) => {
  if (!name) return null;
  const parts = name.split(" ");
  return parts.length > 1 ? parts.slice(1).join(" ") : null;
};

const getOutcomeClass = (outcome: string) => {
  const lower = outcome.toLowerCase();
  if (["closed", "close", "won", "sale", "deposit collected", "upsell"].some(o => lower.includes(o))) return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
  if (lower.includes("no show") || lower.includes("noshow") || lower.includes("no-show")) return "bg-red-500/15 text-red-500";
  if (["cancelled", "canceled", "rescheduled"].includes(lower)) return "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400";
  if (lower.includes("mrr") || lower.includes("downsell") || lower.includes("downsold")) return "bg-blue-500/15 text-blue-500";
  return "bg-muted text-muted-foreground";
};

export function Calls() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [items, setItems] = useState<FirefliesTranscript[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal state
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Open call detail modal
  const openCallDetail = (firefliesId: string) => {
    setSelectedCallId(firefliesId);
    setIsModalOpen(true);
  };

  // Close call detail modal
  const closeCallDetail = () => {
    setIsModalOpen(false);
    setSelectedCallId(null);
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch transcripts
  const fetchTranscripts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        search: debouncedSearch,
      });

      const res = await fetch(`/api/fireflies/list?${params.toString()}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || `Failed to fetch: ${res.status}`);
      }

      const json = (await res.json()) as TranscriptListResponse;
      setItems(json.items);
      setTotalCount(json.totalCount);
    } catch (e) {
      console.error("Error fetching transcripts:", e);
      setError(e instanceof Error ? e.message : "Failed to fetch calls");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchTranscripts();
  }, [fetchTranscripts]);

  // Sync calls from Fireflies
  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setError(null);

      const res = await fetch("/api/fireflies/sync-incremental?pages=3", {
        method: "POST",
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to sync");
      }

      const result = await res.json();
      console.log(`Synced ${result.upserted} transcripts`);

      // Refresh the list
      await fetchTranscripts();
    } catch (e) {
      console.error("Error syncing transcripts:", e);
      setError(e instanceof Error ? e.message : "Failed to sync calls");
    } finally {
      setIsSyncing(false);
    }
  };

  // Table columns
  const Cell = ({ val, className }: { val: string | null; className?: string }) =>
    val ? (
      <span className={`text-sm ${className ?? ""}`}>{val}</span>
    ) : (
      <span className="text-xs text-muted-foreground/40 italic">—</span>
    );

  const columns: ColumnDef<FirefliesTranscript>[] = [
    {
      id: "firstName",
      header: "First Name",
      cell: (row) => <Cell val={firstName(row.prospectName)} className="font-medium" />,
    },
    {
      id: "lastName",
      header: "Last Name",
      cell: (row) => <Cell val={lastName(row.prospectName)} className="font-medium" />,
    },
    {
      id: "email",
      header: "Email",
      cell: (row) => (
        <span className="text-sm text-muted-foreground max-w-[160px] truncate block">{row.prospectEmail ?? <span className="text-xs italic text-muted-foreground/40">—</span>}</span>
      ),
    },
    {
      id: "phone",
      header: "Phone",
      cell: (row) => <Cell val={row.prospectPhone} className="text-muted-foreground whitespace-nowrap" />,
    },
    {
      id: "sourceFunnel",
      header: "Source Funnel",
      cell: (row) => (
        <span className="text-sm text-muted-foreground max-w-[120px] truncate block">{row.sourceFunnel ?? <span className="text-xs italic text-muted-foreground/40">—</span>}</span>
      ),
    },
    {
      id: "sourcePlatform",
      header: "Platform",
      cell: (row) => <Cell val={row.sourcePlatform} className="text-muted-foreground whitespace-nowrap" />,
    },
    {
      id: "sourceContent",
      header: "Source Content",
      cell: (row) => (
        <span className="text-sm text-muted-foreground max-w-[140px] truncate block">{row.sourceContent ?? <span className="text-xs italic text-muted-foreground/40">—</span>}</span>
      ),
    },
    {
      id: "outcome",
      header: "Outcome",
      cell: (row) => {
        const outcome = row.outcome;
        if (!outcome) return <span className="text-xs text-muted-foreground/40 italic">—</span>;
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getOutcomeClass(outcome)}`}>
            {outcome}
          </span>
        );
      },
    },
    {
      id: "recording",
      header: "Recording",
      cell: (row) => (
        <Button
          size="sm"
          onClick={() => openCallDetail(row.firefliesId)}
          className="bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white font-medium px-3 hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all whitespace-nowrap"
        >
          <Play className="h-3.5 w-3.5 mr-1.5" />
          View Call
        </Button>
      ),
      className: "text-right",
      headerClassName: "text-right",
    },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="opacity-0 animate-fade-in-up">
        <h2 className="text-2xl font-bold mb-2">Sales Calls</h2>
        <p className="text-muted-foreground">
          Review recorded sales calls from Fireflies.ai
        </p>
      </div>

      {/* Search & Sync Bar */}
      <div className="bg-card rounded-2xl p-4 sm:p-6 shadow-brand opacity-0 animate-fade-in-up stagger-1">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search Input */}
          <div className="relative flex-1 w-full sm:max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or outcome..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-secondary border-border h-11"
            />
          </div>

          {/* Sync Button */}
          <Button
            onClick={handleSync}
            disabled={isSyncing}
            variant="outline"
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
            {isSyncing ? "Syncing..." : "Sync Calls"}
          </Button>
        </div>

        {/* Stats */}
        <div className="mt-4 flex items-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-[var(--brand-primary)]" />
            <span>{totalCount.toLocaleString()} total calls</span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-xl p-4 opacity-0 animate-fade-in-up">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTranscripts}
            className="mt-3"
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Calls Table */}
      <div className="opacity-0 animate-fade-in-up stagger-2">
        <DataTable
          data={items}
          columns={columns}
          rowKey="_id"
          loading={isLoading}
          emptyMessage={
            debouncedSearch
              ? `No calls found matching "${debouncedSearch}"`
              : "No calls found. Click 'Sync Calls' to fetch from Fireflies."
          }
          serverPagination={{
            currentPage: page,
            pageSize: pageSize,
            totalItems: totalCount,
            totalPages: totalPages,
            pageSizeOptions: [10, 25, 50],
            onPageChange: setPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPage(1);
            },
          }}
        />
      </div>

      {/* Call Detail Modal */}
      <CallDetailModal
        isOpen={isModalOpen}
        onClose={closeCallDetail}
        firefliesId={selectedCallId}
      />
    </div>
  );
}

export default Calls;
